import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type CulinaryContent = {
  id: string;
  hero_image_url: string | null;
  hero_kicker: string | null;
  hero_title: string | null;
  hero_subtitle: string | null;
  inclusions_sentence: string | null;
};

export type MenuType = "breakfast" | "lunch" | "dinner";

export type MenuImage = {
  id: string;
  menu_type: MenuType;
  image_url: string;
  position: number;
};

export type GalleryImage = {
  id: string;
  image_url: string;
  position: number;
};

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

const CONTENT_COLS =
  "id,hero_image_url,hero_kicker,hero_title,hero_subtitle,inclusions_sentence";

// --- Public ---

const pageDataSchema = z
  .object({ experience_slug: z.string().min(1).max(60).optional() })
  .optional();

export const getCulinaryPageData = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => pageDataSchema.parse(i) ?? {})
  .handler(
    async ({
      data,
    }): Promise<{
      content: CulinaryContent | null;
      menus: MenuImage[];
      gallery: GalleryImage[];
    }> => {
      const sb = publicClient();
      const expSlug = data?.experience_slug ?? null;

      const [c, m, g, ov, gx, exp] = await Promise.all([
        sb
          .from("culinary_content")
          .select(CONTENT_COLS)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle(),
        sb
          .from("culinary_menu_images")
          .select("id,menu_type,image_url,position")
          .order("menu_type", { ascending: true })
          .order("position", { ascending: true }),
        sb
          .from("culinary_gallery_images")
          .select("id,image_url,position")
          .order("position", { ascending: true }),
        sb
          .from("page_overrides")
          .select("id,page_key,field_key,experience_id,value_text,value_url")
          .eq("page_key", "culinary"),
        sb
          .from("gallery_image_experiences")
          .select("image_table,image_id,experience_id")
          .eq("image_table", "culinary_gallery_images"),
        expSlug
          ? sb.from("experiences").select("id").eq("slug", expSlug).maybeSingle()
          : Promise.resolve({ data: null, error: null } as const),
      ]);

      if (c.error) throw new Error(c.error.message);
      if (m.error) throw new Error(m.error.message);
      if (g.error) throw new Error(g.error.message);
      if (ov.error) throw new Error(ov.error.message);
      if (gx.error) throw new Error(gx.error.message);
      if ("error" in exp && exp.error) throw new Error(exp.error.message);

      const expId =
        ("data" in exp && exp.data ? (exp.data as { id: string }).id : null) ||
        null;

      let content = (c.data ?? null) as CulinaryContent | null;
      if (content && expId) {
        const byField = new Map<string, { text: string | null; url: string | null }>();
        for (const o of ov.data ?? []) {
          if (o.experience_id === expId) {
            byField.set(o.field_key, { text: o.value_text, url: o.value_url });
          }
        }
        const pick = (key: string, fallback: string | null) => {
          const v = byField.get(key);
          if (!v) return fallback;
          const text = v.text && v.text.trim() !== "" ? v.text : null;
          const url = v.url && v.url.trim() !== "" ? v.url : null;
          return text ?? url ?? fallback;
        };
        content = {
          ...content,
          hero_image_url: pick("hero_image", content.hero_image_url),
          hero_kicker: pick("hero_kicker", content.hero_kicker),
          hero_title: pick("hero_title", content.hero_title),
          hero_subtitle: pick("hero_subtitle", content.hero_subtitle),
          inclusions_sentence: pick(
            "inclusions_sentence",
            content.inclusions_sentence,
          ),
        };
      }

      let gallery = (g.data ?? []) as GalleryImage[];
      if (expId) {
        const byImage = new Map<string, Set<string>>();
        for (const l of gx.data ?? []) {
          const s = byImage.get(l.image_id) ?? new Set<string>();
          s.add(l.experience_id);
          byImage.set(l.image_id, s);
        }
        gallery = gallery.filter((img) => {
          const allow = byImage.get(img.id);
          if (!allow) return true;
          return allow.has(expId);
        });
      }

      return {
        content,
        menus: (m.data ?? []) as MenuImage[],
        gallery,
      };
    },
  );

// --- Admin: content ---

const contentSchema = z.object({
  id: z.string().uuid().optional(),
  hero_image_url: z.string().nullable(),
  hero_kicker: z.string().nullable(),
  hero_title: z.string().nullable(),
  hero_subtitle: z.string().nullable(),
  inclusions_sentence: z.string().nullable(),
});

export const upsertCulinaryContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => contentSchema.parse(data))
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    if (data.id) {
      const { error } = await sb
        .from("culinary_content")
        .update({
          hero_image_url: data.hero_image_url,
          hero_kicker: data.hero_kicker,
          hero_title: data.hero_title,
          hero_subtitle: data.hero_subtitle,
          inclusions_sentence: data.inclusions_sentence,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await sb
      .from("culinary_content")
      .insert({
        hero_image_url: data.hero_image_url,
        hero_kicker: data.hero_kicker,
        hero_title: data.hero_title,
        hero_subtitle: data.hero_subtitle,
        inclusions_sentence: data.inclusions_sentence,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

// --- Admin: menu images ---

export const addMenuImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        menu_type: z.enum(["breakfast", "lunch", "dinner"]),
        image_url: z.string().url(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: existing } = await sb
      .from("culinary_menu_images")
      .select("position")
      .eq("menu_type", data.menu_type)
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { error } = await sb.from("culinary_menu_images").insert({
      menu_type: data.menu_type,
      image_url: data.image_url,
      position: nextPos,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteMenuImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("culinary_menu_images")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderMenuImages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        menu_type: z.enum(["breakfast", "lunch", "dinner"]),
        ordered_ids: z.array(z.string().uuid()),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("culinary_menu_images")
        .update({ position: i })
        .eq("id", data.ordered_ids[i])
        .eq("menu_type", data.menu_type);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// --- Admin: gallery ---

export const addGalleryImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ image_url: z.string().url() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const { data: existing } = await sb
      .from("culinary_gallery_images")
      .select("position")
      .order("position", { ascending: false })
      .limit(1);
    const nextPos = (existing?.[0]?.position ?? -1) + 1;
    const { error } = await sb.from("culinary_gallery_images").insert({
      image_url: data.image_url,
      position: nextPos,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteGalleryImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("culinary_gallery_images")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderGalleryImages = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ ordered_ids: z.array(z.string().uuid()) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await sb
        .from("culinary_gallery_images")
        .update({ position: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const updateMenuImageUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ id: z.string().uuid(), image_url: z.string().url() })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("culinary_menu_images")
      .update({ image_url: data.image_url })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updateGalleryImageUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({ id: z.string().uuid(), image_url: z.string().url() })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("culinary_gallery_images")
      .update({ image_url: data.image_url })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

